import { Pair, usePairs } from '@/store/pairs';
import { useAddress } from '@/store/selector';
import { useCallback } from 'react';
import useSWR from 'swr';

export interface HistoryTradeItem {
    id: number;
    methodId: string | null;
    txHash: string;
    walletAddress: string;
    contractAddress: string;
    txDetail: any | null;
    status: 'NON_PROCESSED' | 'PROCESSED' | string;
    blockNo: number;
    chainId: number;
    pairIndex: number;
    tIndex: number | null;
    initialPosToken: string;
    positionSizeDai: string;
    openPrice: number;
    openActualPrice: number;
    closePrice: number;
    buy: boolean;
    leverage: number;
    tp: number;
    sl: number;
    open: boolean;
    orderType: number;
    spreadReductionId: string | null;
    slippageP: number | null;
    pnl: string | null;
    pnlPercentage: number;
    actualPnl: number;
    actualPnlPercentage: number;
    openInterestVolume: string;
    actualReferrer: string | null;
    totalFees: string;
    pnlNoFee: string;
    createdOnChainAt: string;
    createdAt: string;
    updatedAt: string;
    actualPnlBeforeFee: number;
    actualPnlPercentageBeforeFee: number;
    openFeesPercent: number;
    rolloverFees: number;
    openFees: number;
    daiSentToTrader: number;
    pnlAfterFee: number;
    closeFees: number;
    fundingFees: number;
    collateralBeforeFee: number;

    // --- Bổ sung thêm các trường hiển thị ---
    logoUrl?: string;
    symbol?: string;
    nameDisplay?: string;
    decimals: number; // mặc định 18
    fundingFeesDisplay?: number; // fundingFees / 10^18
    rolloverFeesDisplay?: number; // rolloverFees / 10^18
    decimalToShow?: number; // ví dụ: 5
    pnlNumber: number;
    pnlPercent: number;
    isWin: boolean;
}

// --- Raw shape returned by the new dexhistory API ---
interface DexHistoryTrade {
    chain_id: number;
    close_block: number;
    close_collateral_usdc: string;
    close_fee_usdc: string;
    close_price: string;
    close_reason: string;
    close_tx_hash: string;
    closed_at: number;
    funding_fee_usdc: string;
    id: number;
    leverage: number;
    open_block: number;
    open_block_time: number;
    open_collateral_usdc: string;
    open_exec_price: string;
    open_fee_usdc: string;
    open_price: string;
    open_price_impact_p: string;
    open_tx_hash: string;
    order_kind: string;
    pair_index: number;
    pair_label: string;
    percent_profit: string;
    position_index: number;
    rollover_fee_usdc: string;
    side: 'long' | 'short';
    sl: string;
    tp: string;
    trader: string;
    usdc_sent_to_trader: string;
}

interface DexHistoryResponse {
    meta: {
        decimals: {
            usdc_decimals: number;
            price_decimals: number;
        };
    };
    trades: DexHistoryTrade[];
}

const CLOSE_REASON_TO_ORDER_TYPE: Record<string, number> = {
    tp: 0,
    sl: 1,
    liquidated: 2,
};

export function realizedPnl(
    daiSentToTrader: number,
    initialPosToken: number,
    decimals: number
) {
    initialPosToken = initialPosToken / Math.pow(10, decimals);
    daiSentToTrader = daiSentToTrader / Math.pow(10, decimals);
    if (daiSentToTrader <= 0) {
        return {
            value: -initialPosToken,
            percentage: -100,
        };
    }

    const percentage =
        ((daiSentToTrader - initialPosToken) / initialPosToken) * 100;

    return {
        value: (initialPosToken * percentage) / 100,
        percentage,
    };
}

function mapDexHistoryTrade(
    item: DexHistoryTrade,
    pairsInfo: Pair[]
): HistoryTradeItem {
    const pair = pairsInfo.find(
        (value) => value.pairIndex.toString() === item.pair_index.toString()
    );

    const infoPnl = realizedPnl(
        Number(item.usdc_sent_to_trader),
        Number(item.open_collateral_usdc),
        18
    );

    return {
        id: item.id,
        methodId: null,
        txHash: item.close_tx_hash || item.open_tx_hash,
        walletAddress: item.trader,
        contractAddress: '',
        txDetail: null,
        status: 'PROCESSED',
        blockNo: item.close_block,
        chainId: item.chain_id,
        pairIndex: item.pair_index,
        tIndex: item.position_index,
        initialPosToken: item.open_collateral_usdc,
        positionSizeDai: item.open_collateral_usdc,
        openPrice: Number(item.open_price),
        openActualPrice: Number(item.open_exec_price),
        closePrice: Number(item.close_price),
        buy: item.side === 'long',
        leverage: item.leverage,
        tp: Number(item.tp),
        sl: Number(item.sl),
        open: false,
        orderType: CLOSE_REASON_TO_ORDER_TYPE[item.close_reason] ?? null,
        spreadReductionId: null,
        slippageP: null,
        pnl: null,
        pnlPercentage: Number(item.percent_profit),
        actualPnl: infoPnl.value,
        actualPnlPercentage: infoPnl.percentage,
        openInterestVolume: item.open_collateral_usdc,
        actualReferrer: null,
        totalFees: '0',
        pnlNoFee: '0',
        createdOnChainAt: new Date(item.open_block_time * 1000).toISOString(),
        createdAt: new Date(item.open_block_time * 1000).toISOString(),
        updatedAt: new Date(item.closed_at * 1000).toISOString(),
        actualPnlBeforeFee: infoPnl.value,
        actualPnlPercentageBeforeFee: infoPnl.percentage,
        openFeesPercent: 0,
        rolloverFees: Number(item.rollover_fee_usdc) / Math.pow(10, 18),
        openFees: Number(item.open_fee_usdc) / Math.pow(10, 18),
        daiSentToTrader: Number(item.usdc_sent_to_trader),
        pnlAfterFee: infoPnl.value,
        closeFees: Number(item.close_fee_usdc) / Math.pow(10, 18),
        fundingFees: Number(item.funding_fee_usdc) / Math.pow(10, 18),
        collateralBeforeFee: Number(item.open_collateral_usdc),

        logoUrl: pair?.imageUrl,
        symbol: pair?.symbol,
        nameDisplay: pair?.nameDisplay,
        decimals: 18,
        decimalToShow: pair?.decimalsToShow ?? 5,
        pnlNumber: infoPnl.value,
        pnlPercent: infoPnl.percentage,
        isWin: infoPnl.value > 0,
    };
}

export function useHistoryTrade() {
    const address = useAddress();
    const pairsInfo = usePairs((state) => state.data);

    const fetcher = useCallback(async () => {
        try {
            if (!address) {
                return [];
            }

            const response = await fetch(
                `https://mhga.holdstation.com/dexhistory/wallets/${address}/trades?chain=480&limit=100`
            );
            const result: DexHistoryResponse = await response.json();

            return (result.trades ?? []).map((item) =>
                mapDexHistoryTrade(item, pairsInfo)
            );
        } catch (error) {
            console.error('Error in fetch API:', error);
            return [];
        }
    }, [address, pairsInfo]);

    const data = useSWR<HistoryTradeItem[]>('useHistoryTrade', fetcher);

    return data;
}
